import { useRef, useState } from "react";
import HTMLFlipBook from "react-pageflip";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, FileDown, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

/* Dergi vitrini — coklu sayi destekli. Sayi 02 uretildi ama kullanici onayi bekliyor (yayinlanmadi);
   yayinlamak icin ISSUES listesine geri ekleyin: { key: "sayi02", total: 42, label: {tr:"Sayı 02 — N-of-1",en:"Issue 02 — N-of-1"}, pdf: "/magazine/sayi02/2D-Healthcare-Dergi-Sayi02.pdf" } */
const ISSUES = [
  { key: "sayi01", total: 42, label: { tr: "Sayı 01 — Japonya Farkı", en: "Issue 01 — The Japan Difference" }, pdf: "/magazine/sayi01/2D-Healthcare-Dergi-Sayi01.pdf" },
];
const pagesOf = (key, total) => Array.from({ length: total }, (_, i) => `/magazine/${key}/p${String(i + 1).padStart(2, "0")}.jpg`);

export function MagazineShowcase() {
  const { t, lang } = useLang();
  const bookRef = useRef(null);
  const [issueIdx, setIssueIdx] = useState(0);
  const [page, setPage] = useState(0);
  const issue = ISSUES[issueIdx];
  const pages = pagesOf(issue.key, issue.total);

  return (
    <section id="dergi" data-testid="magazine-showcase" className="relative overflow-hidden py-24 sm:py-32 bg-[linear-gradient(160deg,#16040a_0%,#38101f_48%,#16040a_100%)]">
      <span aria-hidden className="absolute -right-6 top-10 font-magserif text-[26vw] leading-none text-[#C9A96A] opacity-[0.07] select-none pointer-events-none hidden lg:block">雑誌</span>
      <span aria-hidden className="absolute -left-10 bottom-8 font-magserif text-[18vw] leading-none text-[#C9A96A] opacity-[0.05] select-none pointer-events-none hidden lg:block">希望</span>

      <div className="relative max-w-5xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
          <div className="flex items-center justify-center gap-4">
            <span className="h-px w-12 bg-[#C9A96A]/60" />
            <p className="text-[11px] font-semibold tracking-[0.35em] text-[#C9A96A] uppercase">{t("mz_kicker")}</p>
            <span className="h-px w-12 bg-[#C9A96A]/60" />
          </div>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#F5EFE6]">{t("mz_title")}</h2>
          <p className="mt-3 font-magserif italic text-lg text-[#C9A96A]/90">{t("mz_sub")}</p>

          {ISSUES.length > 1 && (
            <div className="mt-7 inline-flex rounded-full border border-[#C9A96A]/30 p-1 bg-black/30" data-testid="mag-issues">
              {ISSUES.map((it, i) => (
                <button
                  key={it.key}
                  data-testid={`mag-issue-${it.key}`}
                  onClick={() => { setIssueIdx(i); setPage(0); }}
                  className={`h-9 px-5 rounded-full text-xs font-heading font-bold tracking-wide transition-colors ${i === issueIdx ? "bg-[#C9A96A] text-[#1d0810]" : "text-[#C9A96A]/70 hover:text-[#C9A96A]"}`}
                >
                  {it.label[lang] || it.label.tr}
                </button>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} className="mt-10">
          <div className="mx-auto max-w-[420px] rounded-md p-[3px] bg-[linear-gradient(140deg,#C9A96A_0%,#6e5522_40%,#C9A96A_60%,#8a6b2f_100%)] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]">
            <div className="bg-[#1d0810] rounded-sm p-2" data-testid="magazine-flipbook">
              <HTMLFlipBook
                key={issue.key}
                ref={bookRef}
                width={380}
                height={537}
                size="stretch"
                minWidth={260}
                maxWidth={400}
                minHeight={368}
                maxHeight={566}
                showCover={true}
                usePortrait={true}
                maxShadowOpacity={0.55}
                flippingTime={750}
                className="mx-auto"
                onFlip={(e) => setPage(e.data)}
              >
                {pages.map((src, i) => (
                  <div key={src} className="bg-[#1d0810]">
                    <img src={src} alt={`${t("mz_page")} ${i + 1}`} loading={i < 3 ? "eager" : "lazy"} className="w-full h-full object-cover select-none" draggable={false} />
                  </div>
                ))}
              </HTMLFlipBook>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-center gap-4">
            <button data-testid="mag-prev" onClick={() => bookRef.current?.pageFlip()?.flipPrev()} disabled={page === 0} className="w-11 h-11 rounded-full border border-[#C9A96A]/40 text-[#C9A96A] flex items-center justify-center hover:bg-[#C9A96A] hover:text-[#1d0810] disabled:opacity-30 transition-colors" aria-label={t("mz_prev")}>
              <ChevronLeft size={18} />
            </button>
            <span data-testid="mag-page-indicator" className="text-xs font-semibold tracking-widest text-[#C9A96A]/80 tabular-nums">{page + 1} / {issue.total}</span>
            <button data-testid="mag-next" onClick={() => bookRef.current?.pageFlip()?.flipNext()} disabled={page >= issue.total - 1} className="w-11 h-11 rounded-full border border-[#C9A96A]/40 text-[#C9A96A] flex items-center justify-center hover:bg-[#C9A96A] hover:text-[#1d0810] disabled:opacity-30 transition-colors" aria-label={t("mz_next")}>
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="mt-6 flex justify-center">
            <a data-testid="mag-download" href={issue.pdf} download className="inline-flex items-center gap-2.5 h-12 px-8 rounded-full bg-[#C9A96A] text-[#1d0810] font-heading font-bold text-sm tracking-wide hover:bg-[#dfc08a] transition-colors shadow-[0_12px_30px_-10px_rgba(201,169,106,0.5)]">
              <FileDown size={17} /> {t("mz_download")}
            </a>
          </div>
          <div className="mt-5 text-center">
            <Link data-testid="mag-to-zarafet" to="/zarafet" className="inline-flex items-center gap-1.5 text-xs text-[#C9A96A]/70 hover:text-[#C9A96A] transition-colors">
              <Sparkles size={12} /> {t("mz_zarafet")}
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
